import { kitTextLocaleOf, type KitTextLocale } from 'fym-shared/src/music24/kitText';
import { DEFAULT_RELEASE_KIT_LOOK, ReleaseKitLook } from 'fym-shared/src/music24/releaseThemes';
import { useGetReleaseKitLookQuery, useGetReleaseKitQuery } from '../../../../redux/api/fymAPI';
import { SerializedKit } from '../../../../redux/api/fymAPI.types';

const NO_PALETTE: string[] = [];

export interface SongTheme {
  /** The kit of the song, or null when nothing is made yet. Undefined while it loads. */
  kit: SerializedKit | null | undefined;
  /**
   * The look that a new asset of the song gets: the kit look, else the saved default of the
   * artist, else Clean (the server reads it in the same order).
   */
  look: ReleaseKitLook;
  palette: string[];
  /** The SmartLink address under the pill of a Story; null: none (or the answer did not come yet). */
  storyLink: string | null;
  /** The release date that the Story render reads (`YYYY-MM-DD`); null: none, or not loaded yet. */
  storyReleaseDate: string | null;
  /** True when the song has a published pre-save page; false: a Story and a feed post say "Coming soon". */
  storyPresave: boolean;
  /**
   * The language of the kit words ("Out now", "7 days to go"): the release kit answer `story.locale`
   * (the setting of the workspace owner). English until the answer comes, or from an older server.
   */
  storyLocale: KitTextLocale;
}

/** The language of the kit words in a release kit answer; English when it has none. */
export function storyLocaleOf(story: { locale?: string } | undefined): KitTextLocale {
  return kitTextLocaleOf(story?.locale);
}

/** The release theme of one song, for the Studio chip, the style picker and the AI reel form. */
export function useSongTheme(accountId: number, catalogItemUuid: string): SongTheme {
  const release = useGetReleaseKitQuery({ accountId, catalogItemUuid });
  const kit = release.data?.kit;
  const savedDefault = useGetReleaseKitLookQuery({ accountId }, { skip: kit !== null });
  const look = kit?.look ?? savedDefault.data?.look ?? DEFAULT_RELEASE_KIT_LOOK;
  return {
    kit,
    look,
    palette: kit?.palette ?? NO_PALETTE,
    storyLink: release.data?.story?.link ?? null,
    storyReleaseDate: release.data?.story?.releaseDate ?? null,
    storyPresave: release.data?.story?.presave ?? true,
    storyLocale: storyLocaleOf(release.data?.story),
  };
}
